import React, { useState, useEffect } from 'react';
import { Menu, X, Home, Image, Film, Map, Mail, Heart, Sparkles, ChevronDown, User } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { id: 'home', label: 'Home', icon: <Home className="w-4 h-4" /> },
    { id: 'moments', label: 'Moments', icon: <Image className="w-4 h-4" /> },
    { id: 'actor', label: 'Actor', icon: <Film className="w-4 h-4" /> },
    { id: 'destinations', label: 'Destinations', icon: <Map className="w-4 h-4" /> },
    { id: 'contact', label: 'Contact', icon: <Mail className="w-4 h-4" /> },
  ];

  const scrollToSection = (sectionId) => {
    setActiveLink(sectionId);
    setIsMenuOpen(false);
    
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-xl shadow-lg py-2 border-b border-pink-100/50' 
            : 'bg-white/90 backdrop-blur-md py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo/Brand - Animated */}
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div 
                className={`p-2 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 group-hover:shadow-lg ${
                  isScrolled ? 'w-10 h-10' : 'w-12 h-12'
                }`}
              >
                <Heart className="w-full h-full text-white transition-all duration-500 group-hover:scale-110" />
              </div>
              
              <div className={`transition-all duration-500 ${isScrolled ? 'scale-95' : 'scale-100'}`}>
                <h1 className="text-xl font-bold text-gray-800 tracking-tight">
                  Fathima <span className="text-rose-600 bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">Zahara</span>
                </h1>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-500 animate-fade-in">Grace</span>
                  <div className="w-1 h-1 bg-rose-400 rounded-full"></div>
                  <span className="text-xs text-gray-500 animate-fade-in animation-delay-100">Dreams</span>
                  <div className="w-1 h-1 bg-rose-400 rounded-full"></div>
                  <span className="text-xs text-gray-500 animate-fade-in animation-delay-200">Elegance</span>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative flex items-center space-x-2 px-5 py-3 rounded-xl transition-all duration-300 transform hover:-translate-y-1 animate-slide-down ${
                    activeLink === item.id
                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-xl shadow-pink-200/50'
                      : 'text-gray-600 hover:text-rose-600 hover:bg-pink-50'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Animated underline for active link */}
                  {activeLink === item.id && (
                    <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3/4 h-0.5 bg-white rounded-full animate-pulse"></div>
                  )}
                  
                  {/* Icon with animation */}
                  <span className={`transition-all duration-300 ${activeLink === item.id ? 'text-white scale-110' : 'text-rose-400'}`}>
                    {item.icon}
                  </span>
                  
                  {/* Label with animation */}
                  <span className="font-medium relative">
                    {item.label}
                    <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-rose-500 transition-all duration-300 ${
                      activeLink === item.id ? 'w-full' : 'group-hover:w-full'
                    }`}></span>
                  </span>
                  
                  {/* Sparkle animation for active link */}
                  {activeLink === item.id && (
                    <div className="absolute -top-2 -right-2">
                      <Sparkles className="w-4 h-4 text-yellow-300 animate-spin-slow" />
                    </div>
                  )}
                </button>
              ))}
            </nav>

            {/* Mobile menu button with animation */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="relative p-3 rounded-xl text-gray-600 hover:text-rose-600 hover:bg-pink-50 transition-all duration-300 group"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-rose-500 opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300"></div>
                
                {/* Menu icon with rotation */}
                <div className="relative z-10 transform transition-transform duration-500">
                  {isMenuOpen ? (
                    <X className="w-6 h-6 rotate-180" />
                  ) : (
                    <Menu className="w-6 h-6 group-hover:scale-110" />
                  )}
                </div>
                
                {/* Pulsing dot */}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full animate-pulse"></div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu with enhanced animations */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-all duration-700 ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ top: isScrolled ? '76px' : '88px' }}
      >
        {/* Backdrop blur */}
        <div 
          className={`absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-700 ${
            isMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        ></div>
        
        {/* Menu content */}
        <div
          className={`absolute top-0 right-0 h-full w-80 max-w-full bg-white/95 backdrop-blur-xl shadow-2xl border-l border-pink-100/50 transform transition-all duration-700 ease-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Menu header */}
          <div className="p-6 border-b border-pink-100">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl shadow-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Navigation</h2>
                <p className="text-sm text-gray-500">Explore all sections</p>
              </div>
            </div>
          </div>
          
          {/* Navigation items */}
          <div className="p-4 space-y-2">
            {navigationItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`w-full flex items-center space-x-4 p-4 rounded-xl transition-all duration-300 transform hover:translate-x-2 ${
                  activeLink === item.id
                    ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon with animation */}
                <div className={`p-2 rounded-lg transition-all duration-300 ${
                  activeLink === item.id
                    ? 'bg-white/20'
                    : 'bg-pink-100'
                }`}>
                  <span className={activeLink === item.id ? 'text-white' : 'text-rose-500'}>
                    {item.icon}
                  </span>
                </div>
                
                {/* Label */}
                <span className="font-medium flex-1 text-left">{item.label}</span>
                
                {/* Active indicator */}
                {activeLink === item.id ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    <ChevronDown className="w-4 h-4 text-white animate-bounce" />
                  </div>
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 transform -rotate-90" />
                )}
              </button>
            ))}
          </div>
          
          {/* User profile section */}
          <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-pink-100">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-800">Fathima Zahara</h3>
                <p className="text-sm text-gray-500">Personal Portfolio</p>
              </div>
            </div>
            
            {/* Stats */}
            {/* <div className="mt-4 grid grid-cols-3 gap-2">
              <div className="text-center p-2 bg-pink-50 rounded-lg">
                <div className="text-sm font-bold text-rose-600">5</div>
                <div className="text-xs text-gray-500">Sections</div>
              </div>
              <div className="text-center p-2 bg-pink-50 rounded-lg">
                <div className="text-sm font-bold text-rose-600">50+</div>
                <div className="text-xs text-gray-500">Images</div>
              </div>
              <div className="text-center p-2 bg-pink-50 rounded-lg">
                <div className="text-sm font-bold text-rose-600">∞</div>
                <div className="text-xs text-gray-500">Memories</div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      {!isMenuOpen && (
        <div className="fixed bottom-8 right-8 z-40 md:block hidden">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className={`p-3 bg-gradient-to-br from-pink-500 to-rose-500 text-white rounded-full shadow-lg transition-all duration-500 transform hover:scale-110 hover:shadow-xl ${
              isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <ChevronDown className="w-5 h-5 transform rotate-180" />
          </button>
        </div>
      )}
    </>
  );
};

// Add these to your global CSS or Tailwind config
const styles = `
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin-slow {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-fade-in {
  animation: fade-in 0.5s ease-out;
}

.animate-slide-down {
  animation: slide-down 0.5s ease-out;
}

.animate-spin-slow {
  animation: spin-slow 3s linear infinite;
}

.animation-delay-100 {
  animation-delay: 100ms;
}

.animation-delay-200 {
  animation-delay: 200ms;
}
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Header;